// Mirrors Liquid's `money` filter so a JS-rendered price matches a
// server-rendered one exactly. Both now derive from the shop's money format,
// which theme.liquid already passes as window.theme.moneyFormat. Previously this
// ignored it and concatenated the currency symbol, so a store whose format is
// "Rs. {{amount}}" rendered "Rs. 50.00" from Liquid and "₹50.00" from JS — the
// two sat side by side wherever a variant switch updated a price.
function formatMoney(cents, format) {
  const moneyFormat = format || window.theme?.moneyFormat || "${{amount}}";
  const value =
    typeof cents === "string" ? cents.replace(/[^0-9.-]/g, "") : cents;
  if (value == null || isNaN(value)) return "";

  // Shopify's amount placeholders differ only in precision and which characters
  // separate thousands and decimals.
  function withDelimiters(amount, precision, thousands, decimal) {
    const fixed = (amount / 100).toFixed(precision);
    const parts = fixed.split(".");
    const whole = parts[0].replace(
      /(\d)(?=(\d\d\d)+(?!\d))/g,
      `$1${thousands}`
    );
    return whole + (parts[1] ? decimal + parts[1] : "");
  }

  const placeholder = /\{\{\s*(\w+)\s*\}\}/;
  const match = moneyFormat.match(placeholder);
  if (!match) return moneyFormat;

  let formatted;
  switch (match[1]) {
    case "amount_no_decimals":
      formatted = withDelimiters(value, 0, ",", ".");
      break;
    case "amount_with_comma_separator":
      formatted = withDelimiters(value, 2, ".", ",");
      break;
    case "amount_no_decimals_with_comma_separator":
      formatted = withDelimiters(value, 0, ".", ",");
      break;
    case "amount_with_apostrophe_separator":
      formatted = withDelimiters(value, 2, "'", ".");
      break;
    case "amount_with_space_separator":
      formatted = withDelimiters(value, 2, " ", ",");
      break;
    case "amount_no_decimals_with_space_separator":
      formatted = withDelimiters(value, 0, " ", ",");
      break;
    case "amount_with_period_and_space_separator":
      formatted = withDelimiters(value, 2, " ", ".");
      break;
    case "amount":
    default:
      formatted = withDelimiters(value, 2, ",", ".");
  }

  return moneyFormat.replace(placeholder, formatted);
}

// Slot-machine roll for a quantity-stepper number. Used everywhere a stepper
// count changes (product cards, product page, variant modal, cart) so the
// effect is consistent.
function animateStepperQuantity(el, newValue) {
  if (!el) return;
  const newText = String(newValue);
  const oldText = (
    el.dataset.value != null ? el.dataset.value : el.textContent
  )
    .toString()
    .trim();
  el.dataset.value = newText;

  if (
    oldText === newText ||
    oldText === "" ||
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  ) {
    el.textContent = newText;
    return;
  }

  const goingUp = Number(newText) > Number(oldText);

  const sizer = document.createElement("span");
  sizer.className = "quantity-roll quantity-roll--sizer";
  sizer.textContent = newText.length >= oldText.length ? newText : oldText;

  const outgoing = document.createElement("span");
  outgoing.className = "quantity-roll quantity-roll--out";
  outgoing.textContent = oldText;

  const incoming = document.createElement("span");
  incoming.className = "quantity-roll quantity-roll--in";
  incoming.textContent = newText;

  el.textContent = "";
  el.classList.add("is-rolling");
  el.dataset.dir = goingUp ? "up" : "down";
  el.append(sizer, outgoing, incoming);

  requestAnimationFrame(() =>
    requestAnimationFrame(() => el.classList.add("is-animating"))
  );

  let done = false;
  const cleanup = () => {
    if (done) return;
    done = true;
    el.classList.remove("is-rolling", "is-animating");
    delete el.dataset.dir;
    el.textContent = newText;
  };
  incoming.addEventListener("transitionend", cleanup, { once: true });
  setTimeout(cleanup, 280);
}

// Single source of truth for the cart item-count badges (header + floating
// cart buttons). Rolls the number with the slot-machine effect for consistency
// with the quantity steppers.
// Shopify CDN images take a `width` param; keep any existing query intact.
function cdnImageUrl(url, width) {
  try {
    const u = new URL(url, window.location.origin);
    u.searchParams.set("width", String(width));
    return u.toString();
  } catch (e) {
    return url;
  }
}

// Rebuild the floating pill's item thumbnails from cart state. The count and
// empty class are handled by renderCartCount; only the images need redrawing,
// and only when they actually changed (this runs on every cart mutation).
function refreshFloatingCartThumbs() {
  const holder = document.querySelector("[data-floating-cart-thumbs]");
  if (!holder) return;

  const wanted = ((window.cart && window.cart.items) || [])
    .map((item) => item.featured_image?.url || item.image)
    .filter(Boolean)
    .slice(0, 3)
    .map((url) => cdnImageUrl(url, 96));

  const current = Array.from(holder.querySelectorAll("img")).map((img) => img.src);
  if (
    current.length === wanted.length &&
    current.every((src, i) => src === wanted[i])
  ) {
    return;
  }

  holder.replaceChildren(
    ...wanted.map((src) => {
      const img = document.createElement("img");
      img.className = "floating-cart__thumb";
      img.src = src;
      img.alt = "";
      img.width = 96;
      img.height = 96;
      img.loading = "lazy";
      return img;
    })
  );
}

// With the theme's own tab bar hidden in the app, the native tab badge is the
// only cart indicator, so it has to track every mutation. renderCartCount is the
// single choke point for that, but it also runs on optimistic stepper taps —
// debounced so rapid tapping sends one message instead of a burst.
let cartBadgeMessageTimer = null;
function sendCartBadgeToApp(count) {
  if (!window.apployBridge || !window.apployAppManager?.isMobileApp?.()) return;
  clearTimeout(cartBadgeMessageTimer);
  cartBadgeMessageTimer = setTimeout(() => {
    try {
      window.apployBridge.sendData({ type: "cartBadgeUpdate", count: count });
    } catch (err) {
      console.error("Error sending cart badge to app:", err);
    }
  }, 200);
}

function renderCartCount(count) {
  count = Math.max(0, count);
  const countText = count === 1 ? "item" : "items";
  document.querySelectorAll(".cart-count").forEach((el) => {
    animateStepperQuantity(el, count);
    el.setAttribute("aria-cart-count", count);
    el.setAttribute("aria-label", `${count} ${countText}`);
  });
  document.querySelectorAll(".cart-count-text").forEach((el) => {
    el.textContent = countText;
  });
  document.querySelectorAll(".cart-button").forEach((el) => {
    el.classList.toggle("cart-button--empty", count === 0);
  });
  document.querySelectorAll("[data-mobile-cart-count]").forEach((el) => {
    el.textContent = count;
    el.classList.toggle("hidden", count === 0);
  });
  refreshFloatingCartThumbs();
  sendCartBadgeToApp(count);
}

// Optimistic quantity controller: updates the UI instantly on every tap, then
// syncs the ABSOLUTE target quantity to Shopify after a short debounce (so
// rapid tapping = one request). Reconciles with the server (e.g. inventory
// caps) and rolls back to the last confirmed value on failure.
const QuantityManager = (function () {
  const DEBOUNCE_MS = 300;
  const pending = {}; // variantId -> { optimistic, timer }

  function confirmedQty(variantId) {
    const item = getCartItem(parseInt(variantId, 10));
    return item ? item.quantity : 0;
  }

  function currentQty(variantId) {
    variantId = parseInt(variantId, 10);
    return pending[variantId] ? pending[variantId].optimistic : confirmedQty(variantId);
  }

  // Server truth + every pending optimistic delta = what the badge should show.
  function updateOptimisticCartCount() {
    let count = (window.cart && window.cart.item_count) || 0;
    for (const id in pending) {
      count += pending[id].optimistic - confirmedQty(id);
    }
    renderCartCount(count);
  }

  function refreshUI(variantId, quantity) {
    document.dispatchEvent(
      new CustomEvent("cart:updated", { detail: { variantId, quantity } })
    );
    updateOptimisticCartCount();
  }

  function set(variantId, quantity) {
    variantId = parseInt(variantId, 10);
    quantity = Math.max(0, quantity);
    const entry =
      pending[variantId] ||
      (pending[variantId] = { optimistic: confirmedQty(variantId), timer: null });
    entry.optimistic = quantity;
    refreshUI(variantId, quantity);
    clearTimeout(entry.timer);
    entry.timer = setTimeout(() => sync(variantId), DEBOUNCE_MS);
  }

  function step(variantId, delta) {
    set(variantId, currentQty(variantId) + delta);
  }

  async function sync(variantId) {
    variantId = parseInt(variantId, 10);
    const entry = pending[variantId];
    if (!entry) return;
    const target = entry.optimistic;

    try {
      // Fresh add (item not yet in the cart) → cart/add.js; existing line →
      // set the absolute quantity via cart/update.js.
      let res;
      if (confirmedQty(variantId) === 0 && target > 0) {
        res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items: [{ id: variantId, quantity: target }] }),
        });
      } else {
        res = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ updates: { [variantId]: target } }),
        });
      }
      if (!res.ok) {
        let message = "Couldn't update cart";
        try {
          const data = await res.json();
          message = data.message || data.description || message;
        } catch {}
        throw new Error(message);
      }
      await res.json();
      await updateCartState(); // pull server truth into window.cart + badges

      // A newer tap rescheduled a sync — let that one settle instead.
      if (!pending[variantId] || pending[variantId].optimistic !== target) return;

      const serverQty = confirmedQty(variantId);
      delete pending[variantId];
      if (serverQty !== target) {
        // e.g. inventory cap: snap to what the server actually accepted
        Toast.warning(`Only ${serverQty} left in stock`, { duration: 2000 });
      }
      refreshUI(variantId, serverQty);
    } catch (err) {
      const serverQty = confirmedQty(variantId);
      delete pending[variantId];
      refreshUI(variantId, serverQty); // roll back to last confirmed value
      Toast.warning(err.message || "Couldn't update cart", { duration: 2000 });
    }
  }

  return { step, set, currentQty, renderCount: updateOptimisticCartCount };
})();
window.QuantityManager = QuantityManager;

async function updateCartState() {
  try {
    const res = await fetch(`${window.Shopify.routes.root}cart.js`);
    const cartData = await res.json();
    window.cart = cartData;

    // Render through QuantityManager so any still-pending optimistic taps are
    // folded into the badge instead of being clobbered by the server count
    // (which is what caused the brief cart-button desync during a debounce).
    if (window.QuantityManager?.renderCount) {
      window.QuantityManager.renderCount();
    } else {
      renderCartCount(cartData.item_count || 0);
    }
  } catch (err) {
    console.error("Error updating cart state:", err);
  }
}

async function changeCartQuantity(variantId, newQuantity) {
  const steppers = document.querySelectorAll(
    `.quantity-stepper[data-variant-id="${variantId}"]`
  );
  steppers.forEach((stepper) => stepper.classList.add("loading"));

  try {
    await updateCart({
      updates: {
        [variantId]: newQuantity,
      },
    });

    // Dispatch event so all UI can refresh
    document.dispatchEvent(
      new CustomEvent("cart:updated", {
        detail: { variantId, quantity: newQuantity },
      })
    );
  } catch (err) {
    console.error("Error changing cart quantity:", err);
    // Toast is already handled in updateCart function
  } finally {
    steppers.forEach((stepper) => stepper.classList.remove("loading"));
  }
}

// Full-text search needs whole words: /search?q=ban finds nothing even though
// /search/suggest?q=ban matches "banana", which is why the suggestions listed
// products but "See all results" landed on an empty page. A trailing wildcard
// makes the last term prefix-match (verified: q=ban → 0 results, q=ban* → 16).
// options[prefix]=last, which the docs describe as the default, made no
// difference on this store.
//
// The `*` is stripped back out wherever the term is displayed, so shoppers never
// see it in the box or the heading.
function searchUrlFor(term) {
  const cleaned = (term || "").trim();
  if (!cleaned) return `${window.Shopify.routes.root}search`;
  // Leave a query alone if it already wildcards, or ends a quoted phrase.
  const wildcarded = /[*"]$/.test(cleaned) ? cleaned : cleaned + "*";
  return `${window.Shopify.routes.root}search?q=${encodeURIComponent(wildcarded)}`;
}
window.searchUrlFor = searchUrlFor;

// === Recently viewed ===
// View history in localStorage, keyed by product handle with a view count and a
// timestamp: {"banana-kela": {count: 3, at: 1755600000000}}. Ordered by count
// descending, most recent first among equal counts.
//
// localStorage, not sessionStorage: the history survives tab closes and is shared
// across tabs — including Apploy's native tabs, which are separate webviews and
// so had separate session storage.
//
// Lives here rather than in the section so the tracking runs on every product
// view whether or not the section is on the page, and so the storage shape has a
// single owner. sections/recently-viewed.liquid reads it through list().
const RecentlyViewed = (function () {
  const KEY = "theme:recently-viewed";
  const MAX_TRACKED = 20;

  function read() {
    try {
      return JSON.parse(localStorage.getItem(KEY)) || {};
    } catch (err) {
      // Private mode or storage disabled: behave as if nothing was ever viewed.
      return {};
    }
  }

  function write(map) {
    try {
      localStorage.setItem(KEY, JSON.stringify(map));
    } catch (err) {
      /* nothing to do — the feature is a nicety, not a requirement */
    }
  }

  function rank(map, exclude) {
    return Object.keys(map)
      .filter((handle) => handle && handle !== exclude)
      .sort(
        (a, b) => map[b].count - map[a].count || map[b].at - map[a].at
      );
  }

  function record(handle) {
    if (!handle) return;
    const map = read();
    const entry = map[handle] || { count: 0, at: 0 };
    entry.count += 1;
    entry.at = Date.now();
    map[handle] = entry;

    // Keep the store bounded: the least-viewed, least-recent entries fall off.
    const keep = rank(map).slice(0, MAX_TRACKED);
    const trimmed = {};
    keep.forEach((h) => {
      trimmed[h] = map[h];
    });
    write(trimmed);
  }

  function list(limit, exclude) {
    return rank(read(), exclude).slice(0, limit || 8);
  }

  // Drop a handle whose product no longer renders (unpublished or deleted).
  function forget(handle) {
    const map = read();
    if (!(handle in map)) return;
    delete map[handle];
    write(map);
  }

  return { record, list, forget };
})();
window.RecentlyViewed = RecentlyViewed;

function getCartAttributes(attribute) {
  return cart.attributes?.[attribute];
}

async function addToCart(variant_id, quantity, productHandle = null) {
  try {
    const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        items: [{ id: parseInt(variant_id), quantity }],
      }),
    });

    if (res.status === 422) {
      const errorData = await res.json();
      const message = errorData.message || "Failed to add item to cart";
      Toast.warning(message, { duration: 2000 });
      return;
    }

    await res.json();
    await updateCartState();
    document.dispatchEvent(
      new CustomEvent("cart:updated", {
        detail: { variantId: parseInt(variant_id, 10), quantity },
      })
    );
  } catch (err) {
    console.error("Error adding to cart:", err);
    Toast.warning("Failed to add item to cart", { duration: 2000 });
  }
}

async function updateCart(updates) {
  try {
    const res = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updates),
    });

    if (res.status === 422) {
      const errorData = await res.json();
      const message = errorData.message || "Failed to update cart";
      Toast.warning(message, { duration: 2000 });
      return;
    }

    await res.json();
    await updateCartState();
  } catch (err) {
    console.error("Error updating cart:", err);
    Toast.warning("Failed to update cart", { duration: 2000 });
  }
}

// A prerendered page is a snapshot: its inline `window.cart` is whatever the
// cart held when the HTML was rendered, and nothing re-reads it on load. Re-sync
// when such a page is activated (and after a bfcache restore) so the badge, the
// floating pill and the drawer can't come back showing a stale count. Shopify
// drops speculation caches on cart mutations too; this is the second line of
// defence, and it costs one /cart.js request only on an actual activation.
function setupCartStateResync() {
  if (document.prerendering) {
    document.addEventListener(
      "prerenderingchange",
      () => {
        updateCartState();
      },
      { once: true }
    );
  }

  window.addEventListener("pageshow", (event) => {
    if (event.persisted) updateCartState();
  });
}

setupCartStateResync();

function getCartItems() {
  return cart.items;
}

function getCartItem(variant_id) {
  return cart.items?.find((item) => item.variant_id === variant_id);
}

// === Shared Helper ===
function updateVariantUI(container, variant, qtySelectorPrefix) {
  const formattedPrice = formatMoney(variant.price);
  const formattedComparePrice = formatMoney(variant.compare_at_price);

  const variantSelect = container.querySelector(
    ".product-card__variant-select"
  );
  if (variantSelect) {
    // Update the variant ID
    variantSelect.dataset.variantId = variant.id;
    // Update the text content (excluding the SVG and spinner)
    const textNode = Array.from(variantSelect.childNodes).find(
      (node) => node.nodeType === Node.TEXT_NODE
    );
    if (textNode) {
      textNode.textContent = variant.title;
    }
  }

  const priceEl = container.querySelector(
    ".product-card__price, .variant-item__price"
  );
  if (priceEl) priceEl.textContent = formattedPrice;

  const comparePriceEl = container.querySelector(
    ".product-card__compare-price, .variant-item__compare-price"
  );
  if (comparePriceEl) {
    if (variant.compare_at_price > variant.price) {
      comparePriceEl.textContent = formattedComparePrice;
      comparePriceEl.style.display = "inline";
    } else {
      comparePriceEl.textContent = "";
      comparePriceEl.style.display = "none";
    }
  }

  const discountBadge = container.querySelector(
    ".product-card__discount-badge, .variant-item__discount-badge"
  );
  if (discountBadge) {
    if (variant.compare_at_price > variant.price) {
      const discountPercentage = Math.floor(
        ((variant.compare_at_price - variant.price) /
          variant.compare_at_price) *
          100
      );
      discountBadge.textContent = `${discountPercentage}% OFF`;
      discountBadge.style.display = "block";
    } else {
      discountBadge.style.display = "none";
    }
  }

  const cartQty = QuantityManager.currentQty(variant.id);

  const qtyStepperWrapper = container.querySelector(
    `.${qtySelectorPrefix}__quantity-stepper-wrapper`
  );
  const qtyDisplay = container.querySelector(".quantity-stepper__quantity");
  const conversionButtonWrapper = container.querySelector(
    `.${qtySelectorPrefix}__conversion-button-wrapper`
  );

  const availableHere = purchasableHere(container, variant.id, variant.available);

  if (cartQty > 0) {
    if (qtyStepperWrapper) qtyStepperWrapper.style.display = "flex";
    if (qtyDisplay) animateStepperQuantity(qtyDisplay, cartQty);
    if (conversionButtonWrapper) conversionButtonWrapper.style.display = "none";
  } else if (availableHere) {
    if (qtyStepperWrapper) qtyStepperWrapper.style.display = "none";
    if (conversionButtonWrapper)
      conversionButtonWrapper.style.display = "inline-flex";
  } else {
    // Variant is not available - hide both stepper and conversion button
    if (qtyStepperWrapper) qtyStepperWrapper.style.display = "none";
    if (conversionButtonWrapper) conversionButtonWrapper.style.display = "none";
  }
}

// Resolved per-variant availability, published by Liquid as data-store-availability.
// The theme's own button recomputes derive state from Shopify's shop-wide
// variant.available, which says "available" whenever ANY location has stock —
// including one the shopper isn't buying from. Liquid has already applied the
// order of authority (Inventric's per-location stock first, Shopify's flag as
// the fallback), so this only looks the answer up.
function purchasableHere(scopeEl, variantId, shopifyAvailable) {
  const holder = scopeEl?.closest?.("[data-store-availability]");
  if (!holder) return shopifyAvailable;
  // "id:1,id:0" — not JSON, because quotes and braces both need escaping inside
  // an attribute and a brace inside a Liquid output tag breaks its lexer. The
  // JSON version shipped a value that threw, and the catch below then handed
  // back Shopify's flag, re-enabling every add button the server had disabled.
  const wanted = String(variantId);
  for (const pair of (holder.dataset.storeAvailability || "").split(",")) {
    const sep = pair.indexOf(":");
    if (sep === -1) continue;
    if (pair.slice(0, sep).trim() === wanted) return pair.slice(sep + 1).trim() === "1";
  }
  // Not listed: no opinion published for this variant, so defer to Shopify.
  return shopifyAvailable;
}
window.purchasableHere = purchasableHere;

// === ProductCard Class ===
class ProductCard {
  constructor(cardEl, productData) {
    this.cardEl = cardEl;
    this.productData = productData;
    this.selectedVariantId = parseInt(cardEl.dataset.selectedVariantId);

    this.variantSelector = cardEl.querySelector(
      ".product-card__variant-select"
    );
    if (this.variantSelector) {
      this.variantSelector.addEventListener(
        "change",
        this.onVariantChange.bind(this)
      );
    }

    this.updateUI(this.selectedVariantId);
    document.addEventListener("cart:updated", (e) => {
      const { variantId } = e.detail || {};
      this.updateUI(variantId);
    });
    document.addEventListener("variant:change", (e) => {
      const { variantId } = e.detail || {};
      this.updateUI(variantId);
    });
  }

  onVariantChange(e) {
    this.selectedVariantId = parseInt(e.target.value, 10);
    this.updateUI(this.selectedVariantId);
  }

  updateUI(variantId) {
    const variant = this.productData.variants.find((v) => v.id === variantId);
    if (!variant) return;

    this.cardEl.dataset.selectedVariantId = variantId;

    updateVariantUI(this.cardEl, variant, "product-card");

    // Handle availability UI updates for product cards
    this.updateAvailabilityUI();
  }

  updateAvailabilityUI() {
    const variant = this.productData.variants.find(
      (v) => v.id === this.selectedVariantId
    );
    if (!variant) return;

    const isAvailable = purchasableHere(
      this.cardEl,
      this.selectedVariantId,
      variant.available
    );
    const cartQty = QuantityManager.currentQty(this.selectedVariantId);

    // Get the UI elements
    const quantityStepperWrapper = this.cardEl.querySelector(
      ".product-card__quantity-stepper-wrapper"
    );
    const conversionButtonWrapper = this.cardEl.querySelector(
      ".product-card__conversion-button-wrapper"
    );
    const soldOutButtonWrapper = this.cardEl.querySelector(
      ".product-card__sold-out-button-wrapper"
    );

    if (cartQty > 0) {
      // Product is in cart - show quantity stepper
      if (quantityStepperWrapper) quantityStepperWrapper.style.display = "flex";
      if (conversionButtonWrapper)
        conversionButtonWrapper.style.display = "none";
      if (soldOutButtonWrapper) soldOutButtonWrapper.style.display = "none";
    } else if (isAvailable) {
      // Product is available but not in cart - show conversion button
      if (quantityStepperWrapper) quantityStepperWrapper.style.display = "none";
      if (conversionButtonWrapper)
        conversionButtonWrapper.style.display = "inline-flex";
      if (soldOutButtonWrapper) soldOutButtonWrapper.style.display = "none";
    } else {
      // Product is not available - show sold out button
      if (quantityStepperWrapper) quantityStepperWrapper.style.display = "none";
      if (conversionButtonWrapper)
        conversionButtonWrapper.style.display = "none";
      if (soldOutButtonWrapper)
        soldOutButtonWrapper.style.display = "inline-flex";
    }
  }
}

// === VariantItem Class ===
class VariantItem {
  constructor(variantEl, variantData, productHandle) {
    this.variantEl = variantEl;
    this.variantData = variantData;
    this.productHandle = productHandle;
    this.variantId = parseInt(variantEl.dataset.variantId, 10);

    this.updateUI(this.variantId);
    document.addEventListener("cart:updated", (e) => {
      const { variantId } = e.detail || {};
      if (variantId === this.variantId) {
        this.updateUI(this.variantId);
        // Store the last interacted variant
        window.lastInteractedVariant = this.variantId;
      }
    });

    this.conversionButton = this.variantEl.querySelector(".conversion-button");
    if (this.conversionButton) {
      this.conversionButton.addEventListener("click", () => {
        // Optimistic add (instant, debounced sync) — no blocking spinner
        window.lastInteractedVariant = this.variantId;
        QuantityManager.step(this.variantId, 1);
      });
    }
  }

  updateUI(variantId) {
    const variant = this.variantData;
    if (!variant || variant.id !== variantId) return;

    updateVariantUI(this.variantEl, variant, "variant-item");
  }
}
class Cart {
  constructor(cartSectionEl) {
    this.cartSectionEl = cartSectionEl;

    // Initial UI update
    this.updateUI();
    document.addEventListener("cart:updated", () => {
      this.updateUI();
    });
  }

  updateUI() {
    const root = this.cartSectionEl;
    const isCartEmpty = cart.item_count === 0;
    // Query fresh each call so the UI survives the node swaps done by
    // refreshCartDrawer (it replaces the items / summary / footer regions).
    const cartEmptyEl = root.querySelector(".cart-empty");
    const cartHideEls = root.querySelectorAll(
      ".cart-content__section, .cart-content__footer, [data-free-shipping], .cart-content__app-block"
    );
    if (isCartEmpty) {
      cartEmptyEl?.classList.remove("cart-hidden-content");
      cartHideEls.forEach((el) => el.classList.add("cart-hidden-content"));
      return;
    } else {
      cartEmptyEl?.classList.add("cart-hidden-content");
      cartHideEls.forEach((el) => el.classList.remove("cart-hidden-content"));
    }
    // Update quantity steppers in cart
    this.cartSectionEl.querySelectorAll(".cart-item").forEach((cartItemEl) => {
      const stepperEl = cartItemEl.querySelector(".quantity-stepper");
      const qtyDisplay = stepperEl?.querySelector(
        ".quantity-stepper__quantity"
      );
      const variantId = parseInt(stepperEl?.dataset.variantId, 10);
      // Use the optimistic quantity so cart steppers give instant feedback too
      const qty = QuantityManager.currentQty(variantId);
      if (qty > 0 && qtyDisplay) {
        animateStepperQuantity(qtyDisplay, qty);
      } else if (qty <= 0) {
        cartItemEl.remove();
      }
    });

    // Item count
    const itemCountEl = this.cartSectionEl.querySelector(
      ".cart-content__section-header span:first-child"
    );
    if (itemCountEl) {
      itemCountEl.textContent = `Items: ${cart.item_count}`;
    }

    // Total price in section header
    const totalPriceEl = this.cartSectionEl.querySelector(
      ".cart-content__section-header span:last-child"
    );
    if (totalPriceEl) {
      totalPriceEl.textContent = formatMoney(cart.total_price);
    }

    // Item subtotal
    const itemSubtotalEl = this.cartSectionEl.querySelector(
      ".cart-summary__item:nth-child(1) span:last-child"
    );
    if (itemSubtotalEl) {
      itemSubtotalEl.textContent = formatMoney(cart.items_subtotal_price);
    }

    // Discount
    const discountEl = this.cartSectionEl.querySelector(
      ".cart-summary__item:nth-child(2) span:last-child"
    );
    if (discountEl) {
      discountEl.textContent = formatMoney(cart.total_discount);
    }

    // Payable
    const payableEl = this.cartSectionEl.querySelector(
      ".cart-summary__item:nth-child(3) span:last-child"
    );
    if (payableEl) {
      payableEl.textContent = formatMoney(cart.total_price);
    }

    // Footer total
    const footerTotalEl = this.cartSectionEl.querySelector(
      ".cart-content__footer > span"
    );
    if (footerTotalEl) {
      footerTotalEl.textContent = formatMoney(cart.total_price);
    }

    // Free delivery progress bar
    const freeShippingEl = this.cartSectionEl.querySelector(
      "[data-free-shipping]"
    );
    if (freeShippingEl) {
      const threshold = parseInt(freeShippingEl.dataset.threshold, 10);
      if (threshold > 0) {
        const remaining = threshold - cart.total_price;
        const fillEl = freeShippingEl.querySelector("[data-free-shipping-fill]");
        const messageEl = freeShippingEl.querySelector(
          "[data-free-shipping-message]"
        );
        const progress = Math.min(
          100,
          Math.round((cart.total_price / threshold) * 100)
        );
        if (fillEl) fillEl.style.width = `${progress}%`;
        if (messageEl) {
          messageEl.innerHTML =
            remaining <= 0
              ? "🎉 You've unlocked <strong>free delivery!</strong>"
              : `Add <strong>${formatMoney(
                  remaining
                )}</strong> more for free delivery`;
        }
      }
    }
  }
}

// === Toast Notification System ===
class Toast {
  constructor(message, options = {}) {
    this.message = message;
    this.options = {
      duration: 5000, // Default 5 seconds
      type: "info", // 'info', 'success', 'error', 'warning'
      dismissible: true,
      ...options,
    };

    this.element = null;
    this.timeoutId = null;
    this.init();
  }

  init() {
    this.createToastElement();
    this.show();
    this.setupAutoHide();
  }

  createToastElement() {
    // Clone the template
    const template = document.getElementById("toast-template");
    if (!template) {
      console.error("Toast template not found");
      return;
    }

    const toast = template.cloneNode(true);
    toast.id = ""; // Remove the template ID
    toast.style.display = "flex"; // Show the cloned element

    // Update the toast type class
    toast.className = `toast toast--${this.options.type}`;

    // Update the message
    const messageEl = toast.querySelector(".toast__message");
    if (messageEl) {
      messageEl.textContent = this.message;
    }

    // Handle dismissible option
    if (!this.options.dismissible) {
      const closeBtn = toast.querySelector(".toast__close");
      if (closeBtn) {
        closeBtn.style.display = "none";
      }
    }

    // Setup close button event
    const closeBtn = toast.querySelector(".toast__close");
    if (closeBtn) {
      closeBtn.addEventListener("click", () => {
        this.hide();
      });
    }

    this.element = toast;

    // Add to container
    const container = document.getElementById("toast-container");
    if (container) {
      container.appendChild(toast);
    }
  }

  show() {
    // Force reflow to ensure transition works
    this.element.offsetHeight;
    this.element.classList.add("toast--show");
  }

  hide() {
    this.element.classList.add("toast--hide");

    // Remove element after animation
    setTimeout(() => {
      if (this.element && this.element.parentNode) {
        this.element.parentNode.removeChild(this.element);
      }
    }, 300);
  }

  setupAutoHide() {
    if (this.options.duration > 0) {
      this.timeoutId = setTimeout(() => {
        this.hide();
      }, this.options.duration);
    }
  }

  // Static methods for easy usage
  static show(message, options = {}) {
    return new Toast(message, options);
  }

  static success(message, options = {}) {
    return new Toast(message, { ...options, type: "success" });
  }

  static error(message, options = {}) {
    return new Toast(message, { ...options, type: "error" });
  }

  static warning(message, options = {}) {
    return new Toast(message, { ...options, type: "warning" });
  }

  static info(message, options = {}) {
    return new Toast(message, { ...options, type: "info" });
  }
}

// === Main App ===
(function () {
  // Function to initialize product cards and conversion buttons
  function initializeProductCards() {
    // Existing product card initialization
    document.querySelectorAll(".product-card").forEach((cardEl) => {
      // Guard: only initialize each card once. Without this, every AJAX
      // re-render (collection filter / infinite scroll / search) re-wrapped all
      // existing cards, stacking duplicate document-level listeners per card.
      if (cardEl.dataset.pcInit === "1") return;
      const productHandle = cardEl.dataset.productHandle;
      const productData = window.productData?.[productHandle];
      if (productData) {
        cardEl.dataset.pcInit = "1";
        new ProductCard(cardEl, productData);
      }
    });
  }

  // Create a reusable conversion button click handler
  function createConversionButtonHandler(btn) {
    return async function () {
      const productCard = btn.closest(".product-card");

      const ageRestricted = productCard.dataset.ageRestricted === "true";
      const productHandle = productCard.dataset.productHandle;
      const variantsCount = parseInt(productCard.dataset.variantsCount, 10);
      const variantId = productCard.dataset.selectedVariantId;

      const ageRestrictionBackdrop = document.getElementById(
        "age-restriction-backdrop"
      );
      const cartOptIn = getCartAttributes("age-restriction-opt-in");

      const action = async () => {
        if (variantsCount > 1) {
          // Opening the variant picker is a real fetch — keep a brief spinner
          btn.classList.add("spinner");
          try {
            await showVariantsList(productHandle);
          } catch (err) {
            console.error("Error opening variants:", err);
          } finally {
            btn.classList.remove("spinner");
          }
        } else {
          // Single variant: optimistic add (instant, debounced sync)
          QuantityManager.step(variantId, 1);
        }
      };

      try {
        if (ageRestricted && ageRestrictionBackdrop) {
          if (cartOptIn === "Yes") {
            await action();
          } else {
            await showAgeRestrictionModal();
            await action();
          }
        } else {
          await action();
        }
      } catch {}
    };
  }

  function initializeConversionButtons(container = document) {
    // === Conversion Button ===
    const conversionButtons = container.querySelectorAll(
      ".conversion-button,.product-card__variant-select"
    );

    conversionButtons.forEach((btn) => {
      // Remove existing listeners to avoid duplicates
      btn.removeEventListener("click", btn._clickHandler);

      // Create and store the click handler
      btn._clickHandler = createConversionButtonHandler(btn);

      // Add the new listener
      btn.addEventListener("click", btn._clickHandler);
    });
  }

  // Make functions globally available
  window.initializeProductCards = initializeProductCards;
  window.initializeConversionButtons = initializeConversionButtons;

  // === Global Functions ===
  async function showVariantsList(product_handle) {
    const res = await fetch(
      `${window.Shopify.routes.root}products/${product_handle}?sections=variants-list`
    );
    const resJson = await res.json();
    const sectionHtml = resJson["variants-list"];

    const oldSection = document.getElementById("shopify-section-variants-list");
    if (oldSection) oldSection.remove();

    const mainContent = document.getElementById("main-content");
    if (sectionHtml && mainContent) {
      mainContent.insertAdjacentHTML("afterend", sectionHtml);
      setTimeout(() => {
        document
          .querySelector(".variants-list-backdrop")
          ?.classList.add("active");
        document.querySelectorAll(".variant-item").forEach((variantEl) => {
          const variantId = parseInt(variantEl.dataset.variantId, 10);
          const productHandle = document.querySelector(
            ".variants-list-backdrop"
          )?.dataset.productHandle;
          const productData = window.productData[productHandle];
          const variantData = productData?.variants?.find(
            (v) => v.id === variantId
          );

          if (variantData) {
            new VariantItem(variantEl, variantData, productHandle);
          }
        });
      }, 10);
      // No scroll lock for the variant modal: the backdrop is fixed, so the
      // page is free to scroll behind it.
    }
  }

  function showAgeRestrictionModal() {
    return new Promise((resolve, reject) => {
      const backdrop = document.getElementById("age-restriction-backdrop");
      backdrop.classList.add("active");
      openModal();

      const buttons = backdrop.querySelectorAll("button");

      async function clickHandler(e) {
        if (this.classList.contains("age-restriction-continue")) {
          // Disable the button and show spinner
          this.disabled = true;
          this.classList.add("spinner");

          try {
            await updateCart({
              attributes: { "age-restriction-opt-in": "Yes" },
            });
            cleanup();
            resolve();
          } catch (err) {
            console.error("Error updating cart:", err);
            // Re-enable the button and remove spinner on error
            this.disabled = false;
            this.classList.remove("spinner");
            cleanup();
            reject(err);
          }
        } else if (this.classList.contains("age-restriction-cancel")) {
          cleanup();
          reject();
        }
      }

      function cleanup() {
        backdrop.classList.remove("active");
        buttons.forEach((btn) => {
          btn.removeEventListener("click", clickHandler);
          // Ensure button is re-enabled and spinner is removed
          btn.disabled = false;
          btn.classList.remove("spinner");
        });
        closeModal();
      }

      buttons.forEach((btn) => btn.addEventListener("click", clickHandler));
    });
  }

  // Make global functions available
  window.showVariantsList = showVariantsList;
  window.showAgeRestrictionModal = showAgeRestrictionModal;
  window.addToCart = addToCart;
  window.updateCart = updateCart;
  window.getCartAttributes = getCartAttributes;
  window.updateVariantUI = updateVariantUI;
  window.getCartItem = getCartItem;
  window.formatMoney = formatMoney;
  window.changeCartQuantity = changeCartQuantity;
  window.updateCartState = updateCartState;

  // Make Toast class globally available
  window.Toast = Toast;

  // === Global Cart Functions ===
  // Refresh only the cart's dependent regions (free-delivery bar, items,
  // summary, footer) from a fresh server render, leaving the app-block slot
  // untouched. The drawer is server-rendered once in the header group so its
  // app blocks persist; the Section Rendering API can't render app blocks, so
  // we never replace the whole drawer — only these data-marked regions.
  async function refreshCartDrawer() {
    const drawer = document.getElementById("cart-backdrop");
    if (!drawer) return;
    try {
      const res = await fetch(
        `${window.Shopify.routes.root}?sections=cart-drawer`
      );
      if (!res.ok) return;
      const json = await res.json();
      const html = json["cart-drawer"];
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, "text/html");
      ["[data-free-shipping]", "[data-cart-items]", "[data-cart-summary]", "[data-cart-footer]"].forEach(
        (sel) => {
          const fresh = doc.querySelector(sel);
          const current = drawer.querySelector(sel);
          if (fresh && current) current.replaceWith(fresh);
        }
      );
    } catch (err) {
      console.error("Error refreshing cart drawer:", err);
    }
  }

  // Element focus returns to when the drawer closes (the control that opened it).
  let lastCartTrigger = null;

  function drawerFocusable(drawer) {
    return Array.from(
      drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => el.offsetParent !== null);
  }

  // Focus trap: keep Tab / Shift+Tab inside the open drawer.
  function trapCartFocus(e) {
    if (e.key !== "Tab") return;
    const drawer = document.getElementById("cart-backdrop");
    if (!drawer || !drawer.classList.contains("active")) return;
    const focusable = drawerFocusable(drawer);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  async function showCart() {
    const drawer = document.getElementById("cart-backdrop");
    if (!drawer) {
      // Either cart type is "page" — or the case that looks exactly like a
      // broken setting: cart type is "drawer" but the cart-drawer section isn't
      // in this store's header group. sections/*.json is merge=ours in
      // .gitattributes, so a store whose header group predates the section never
      // receives it from main and this silently redirected instead.
      if (window.theme?.cartType !== "page") {
        console.warn(
          '[theme] Cart type is "drawer" but no cart-drawer section is rendered, ' +
            "so the cart button is falling back to /cart. Add it once for this " +
            "store: theme editor → Header group → Add section → Cart drawer."
        );
      }
      window.location.href = `${window.Shopify.routes.root}cart`;
      return;
    }
    lastCartTrigger = document.activeElement;
    await refreshCartDrawer();
    drawer.removeAttribute("inert");
    drawer.classList.add("active");
    const panel = drawer.querySelector(".cart-content");
    panel?.classList.add("active");
    document.body.classList.add("overlay-open");
    syncModalState();
    document.addEventListener("keydown", trapCartFocus);
    // Re-sync visibility / optimistic quantities against the swapped-in nodes.
    document.dispatchEvent(new CustomEvent("cart:updated", { detail: {} }));
    // Move focus into the dialog.
    (drawer.querySelector(".cart-content__close") || panel)?.focus();
  }

  function hideCart() {
    const drawer = document.getElementById("cart-backdrop");
    if (!drawer) return;
    drawer.classList.remove("active");
    drawer.querySelector(".cart-content")?.classList.remove("active");
    drawer.setAttribute("inert", "");
    document.body.classList.remove("overlay-open");
    syncModalState();
    document.removeEventListener("keydown", trapCartFocus);
    // Return focus to whatever opened the drawer.
    if (lastCartTrigger && document.contains(lastCartTrigger)) {
      lastCartTrigger.focus();
    }
    lastCartTrigger = null;
  }

  // Make cart functions globally available
  window.showCart = showCart;
  window.hideCart = hideCart;

  // product.js calls these for the image-zoom overlay. They live inside this
  // IIFE, so without exporting them that call threw "openModal is not defined",
  // which aborted the rest of the handler: the overlay never set overlay-open
  // (so nothing hid behind it), autoplay never paused, and on close startAuto()
  // was never reached — leaving a multi-image gallery permanently stopped after
  // one zoom.
  window.openModal = openModal;
  window.closeModal = closeModal;

  window.goBack = () => {
    // Apploy's bridge reference is explicit that window.ReactNativeWebView must
    // not be used directly — apployBridge is the supported surface, and it does
    // its own serialisation.
    if (window.apployBridge && window.apployAppManager?.isMobileApp?.()) {
      window.apployBridge.sendData({ type: "goBackToPreviousPage" });
      return;
    }
    history.back();
  };

  // Apploy reserves space for its native tab bar by rendering the webview above
  // it, so the theme's bottom-edge surfaces need no inset in that state. When the
  // native bar is hidden the webview reaches the screen edge and that reservation
  // is gone, so flag it and let --safe-bottom in critical.css add the inset.
  //
  // Read once per page load: each in-app navigation is a fresh load, and the
  // bridge exposes no change event. On timeout or an older app build we keep the
  // default (bar visible, which is Apploy's own default) rather than adding
  // phantom space at the bottom of every page.
  async function syncAppNativeNavState() {
    if (!window.apployBridge || !window.apployAppManager?.isMobileApp?.()) return;
    try {
      const res = await window.apployBridge.requestData({
        type: "getBottomNavigationVisibility",
      });
      const visible = res?.data?.visible ?? res?.visible;
      document.documentElement.classList.toggle(
        "app-native-nav-hidden",
        visible === false
      );
    } catch (err) {
      console.warn(
        "[theme] getBottomNavigationVisibility unavailable; assuming the native tab bar is visible.",
        err
      );
    }
  }

  // The shell knows whether there's anywhere to go back to. Only an explicit
  // `false` hides anything: while the bridge is still injecting the property is
  // undefined, and hiding then would be wrong — so re-check a few times instead
  // of deciding from a value that hasn't arrived. Each in-app navigation is a
  // fresh page load, so this settles once per page.
  function syncAppBackControls(attempt) {
    if (!window.apployAppManager?.isMobileApp?.()) return;

    const canGoBack = window.apployAppManager.apployCanGoBack;
    if (canGoBack === undefined && (attempt || 0) < 3) {
      setTimeout(() => syncAppBackControls((attempt || 0) + 1), 250);
      return;
    }
    if (canGoBack !== false) return;

    document.querySelectorAll(".header__back").forEach((el) => {
      el.hidden = true;
    });

    // Apploy's floating cart widget opens /cart as a native bottom sheet with its
    // own close control and nothing behind it. Search would take the shopper out
    // of that sheet with no way back, so the icon goes too — on the cart only,
    // where that entry point exists.
    if (document.body.dataset.template === "cart") {
      document.querySelectorAll(".header__search").forEach((el) => {
        el.hidden = true;
      });
    }
  }

  // --- Modal awareness ----------------------------------------------------
  //
  // The theme's own overlays announce themselves with body.overlay-open, but
  // merchant apps have their own modals and there is no shared convention:
  // Addressly locks the page with html/body.modal-open, others just drop a
  // dialog into the DOM. Rather than integrate app by app, detect the shapes
  // they all end up producing and publish one answer — body.any-modal-open —
  // that the theme's CSS and the Apploy bridge both key off.
  //
  // Note the direction: this READS overlay-open and never writes it. Writing
  // would feed the detector its own output and latch it on.
  const MODAL_SELECTOR = '[role="dialog"][aria-modal="true"], dialog[open]';

  function isModalShowing(el) {
    // Rendered-but-parked panels are the norm here: the cart drawer sits fully
    // laid out and translated off-screen behind `inert`, so neither a box check
    // nor checkVisibility() would call it closed on its own.
    if (el.closest("[inert]")) return false;
    if (el.getAttribute("aria-hidden") === "true") return false;

    if (typeof el.checkVisibility === "function") {
      if (!el.checkVisibility({ visibilityProperty: true, opacityProperty: true })) {
        return false;
      }
    } else if (getComputedStyle(el).visibility === "hidden") {
      return false;
    }

    // Finally the geometry, which is what catches the parked drawer.
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return false;
    return (
      rect.bottom > 0 &&
      rect.right > 0 &&
      rect.top < window.innerHeight &&
      rect.left < window.innerWidth
    );
  }

  function anyModalOpen() {
    if (document.body.classList.contains("overlay-open")) return true;
    // Addressly's page lock — see the note above openModal() for why the theme
    // deliberately does not use this class name itself.
    if (
      document.body.classList.contains("modal-open") ||
      document.documentElement.classList.contains("modal-open")
    ) {
      return true;
    }
    return Array.prototype.some.call(
      document.querySelectorAll(MODAL_SELECTOR),
      isModalShowing
    );
  }

  // Apploy's native tab bar sits above the webview, so a modal rendered by the
  // page cannot cover it. Hiding it is only ours to undo if we were the ones who
  // hid it: Apploy hides it by itself on secondary pages, and "restoring" it
  // there would raise a bar the shopper never had.
  let navHiddenForModal = false;
  function syncAppNativeNav(modalOpen) {
    if (!window.apployBridge || !window.apployAppManager?.isMobileApp?.()) return;
    const root = document.documentElement;

    if (modalOpen) {
      if (navHiddenForModal) return;
      // Already hidden by the shell: nothing to do, and nothing to remember.
      if (root.classList.contains("app-native-nav-hidden")) return;
      navHiddenForModal = true;
    } else {
      if (!navHiddenForModal) return;
      navHiddenForModal = false;
    }

    // The class drives --safe-bottom: with the bar gone the webview reaches the
    // screen edge, so the modal's own sticky footer needs the inset the bar was
    // standing in for.
    root.classList.toggle("app-native-nav-hidden", modalOpen);

    try {
      window.apployBridge.sendData({
        type: "setBottomNavigationVisibility",
        visible: !modalOpen,
        transitionDuration: 240,
      });
    } catch (err) {
      console.error("Error setting bottom navigation visibility:", err);
    }
  }

  function syncModalState() {
    const open = anyModalOpen();
    document.body.classList.toggle("any-modal-open", open);
    syncAppFloatingCart();
    syncAppNativeNav(open);
  }
  window.syncModalState = syncModalState;

  // App modals arrive with no notification, so watch for them. Two observers
  // rather than one subtree-wide attribute watch: class changes on the two root
  // elements cover the theme's overlays and the apps that lock the page, and the
  // subtree watch is narrowed to the attributes that actually open a dialog.
  function watchForModals() {
    let queued = false;
    function schedule() {
      if (queued) return;
      queued = true;
      // rAF, not a timeout: isModalShowing() measures geometry, so it has to run
      // after the mutation has been laid out.
      requestAnimationFrame(() => {
        queued = false;
        syncModalState();
      });
    }

    const roots = new MutationObserver(schedule);
    roots.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    roots.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    new MutationObserver(schedule).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [
        "role",
        "aria-modal",
        "aria-hidden",
        "open",
        "inert",
        "hidden",
        "style",
      ],
    });
  }

  // Apploy shows its own floating cart widget in the app. What it should do while
  // the theme occupies the bottom of the screen: hide where the theme's own UI
  // replaces it or covers the screen, and on the product page stay reachable but
  // ride above the sticky add-to-cart bar — that's what bottomOffset is for.
  function appFloatingCartState() {
    // Anything modal owns the whole screen, whoever opened it.
    if (anyModalOpen()) return { visible: false, bottomOffset: 0 };

    const template = document.body.dataset.template;

    // The cart page IS the cart — a floating shortcut to it is noise, and the
    // pinned payment footer is already there.
    if (template === "cart") return { visible: false, bottomOffset: 0 };

    if (template === "product") {
      const bar = document.querySelector(".product-actions");
      // Only when the bar is actually pinned: it's a normal in-flow element at
      // desktop widths, where nothing needs lifting.
      if (bar && getComputedStyle(bar).position === "fixed") {
        // Measured rather than hard-coded — the bar grows when the safe-area
        // inset applies (Apploy hides the native tab bar on secondary pages) and
        // when the stepper replaces the add button.
        const height = Math.round(bar.getBoundingClientRect().height);
        return { visible: true, bottomOffset: height + 12 };
      }
    }

    return { visible: true, bottomOffset: 0 };
  }

  let appFloatingCartSignature = null;
  function syncAppFloatingCart() {
    if (!window.apployBridge || !window.apployAppManager?.isMobileApp?.()) return;

    const state = appFloatingCartState();
    // Overlay toggles and resize observations fire this repeatedly; only send
    // when the resulting state actually differs.
    const signature = state.visible + ":" + state.bottomOffset;
    if (signature === appFloatingCartSignature) return;
    appFloatingCartSignature = signature;

    try {
      window.apployBridge.sendData({
        type: "setFloatingCartVisibility",
        visible: state.visible,
        bottomOffset: state.bottomOffset,
        transitionDuration: 240,
      });
    } catch (err) {
      console.error("Error setting floating cart visibility:", err);
    }
  }
  window.syncAppFloatingCart = syncAppFloatingCart;

  // The bar's height isn't final at first paint — the safe-area inset arrives
  // with the bridge's bottom-nav answer, and the stepper swaps in at a different
  // height — so track it rather than measuring once.
  function watchAppFloatingCartOffset() {
    if (document.body.dataset.template !== "product") return;
    if (typeof ResizeObserver !== "function") return;
    const bar = document.querySelector(".product-actions");
    if (!bar) return;
    new ResizeObserver(() => syncAppFloatingCart()).observe(bar);
  }

  // `overlay-open` is a state hook only: page scroll is intentionally left alone,
  // and nothing captures or restores a scroll position (with no lock there's
  // nothing to restore, and scrolling the shopper back to where they were when
  // the overlay opened would fight them).
  //
  // The name matters. It used to be `modal-open`, which addressly.css locks with
  // `html.modal-open, body.modal-open { overflow: hidden !important; position:
  // fixed !important }` — so the theme's overlays inherited the app's scroll lock
  // (and its jump to the top) no matter what this theme's own CSS said.
  function openModal() {
    document.body.classList.add("overlay-open");
    syncModalState();
  }
  function closeModal() {
    document.body.classList.remove("overlay-open");
    syncModalState();
  }

  // Collapse the primary header's search row while the page scrolls down and
  // bring it back on the way up, so the sticky header keeps less of the
  // viewport on mobile. The class goes on the sticky container; the animation
  // lives in critical.css.
  function setupHeaderSearchAutoHide() {
    const container = document.querySelector(".header-container");
    if (!container || !container.querySelector(".header-primary")) return;

    // Leave the row alone near the top of the page, and require a deliberate
    // gesture in each direction so small scroll jitter doesn't flap it.
    const ENGAGE_AFTER = 72;
    const HIDE_DELTA = 6;
    const REVEAL_DELTA = 6;

    let lastY = window.scrollY;
    let ticking = false;

    function update() {
      ticking = false;
      const y = Math.max(0, window.scrollY);
      const delta = y - lastY;
      lastY = y;

      // Never hide the row out from under someone typing in it.
      if (container.contains(document.activeElement)) {
        container.classList.remove("is-search-hidden");
        return;
      }

      if (y <= ENGAGE_AFTER) {
        container.classList.remove("is-search-hidden");
      } else if (delta > HIDE_DELTA) {
        container.classList.add("is-search-hidden");
      } else if (delta < -REVEAL_DELTA) {
        container.classList.remove("is-search-hidden");
      }
    }

    window.addEventListener(
      "scroll",
      () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      },
      { passive: true }
    );

    // Focusing the field (e.g. via keyboard) must always reveal it.
    container.addEventListener("focusin", () => {
      container.classList.remove("is-search-hidden");
    });
  }

  document.addEventListener("DOMContentLoaded", () => {
    setupHeaderSearchAutoHide();
    syncAppBackControls();
    syncAppNativeNavState();
    syncModalState();
    watchAppFloatingCartOffset();
    watchForModals();

    // Count this product view. Scoped to the product section's own wrapper on
    // purpose: [data-product-handle] also appears on cart items and product
    // cards, and the cart drawer renders before <main>, so a document-wide query
    // records whatever happens to be in the cart instead of the product being
    // viewed. Nothing here depends on the recently-viewed section existing.
    //
    // Deferred while prerendering: this theme prerenders product links on hover
    // (snippets/speculation-rules.liquid), and a prerendered page runs its
    // scripts. Recording here would count products the shopper merely hovered
    // past, inflating counts and reordering the rail behind their back.
    if (document.body.dataset.template === "product") {
      const viewed = document.querySelector(".product-wrapper[data-product-handle]");
      const handle = viewed?.dataset.productHandle;
      if (document.prerendering) {
        document.addEventListener(
          "prerenderingchange",
          () => {
            RecentlyViewed.record(handle);
          },
          { once: true }
        );
      } else {
        RecentlyViewed.record(handle);
      }
    }

    // Secondary Header Search Toggle
    const secondaryHeader = document.querySelector(".header-secondary");
    if (secondaryHeader) {
      const searchToggle = secondaryHeader.querySelector(".header__search");
      const titleContent = secondaryHeader.querySelector(
        ".header__secondary-content:not(.header__secondary-content--search)"
      );
      const searchContent = secondaryHeader.querySelector(
        ".header__secondary-content--search"
      );
      const searchInput = searchContent?.querySelector('input[type="search"]');

      if (searchToggle && titleContent && searchContent) {
        // Toggle search on icon click
        searchToggle.addEventListener("click", () => {
          titleContent.style.display = "none";
          searchContent.style.display = "flex";
          searchToggle.setAttribute("aria-expanded", "true");
          if (searchInput) {
            searchInput.focus();
          }
        });

        // Reset header on search input blur only if empty
        if (searchInput) {
          searchInput.addEventListener("blur", () => {
            if (!searchInput.value.trim()) {
              titleContent.style.display = "flex";
              searchContent.style.display = "none";
              searchToggle.setAttribute("aria-expanded", "false");
            }
          });
        }
      }
    }

    // Initialize product cards and conversion buttons
    initializeProductCards();
    initializeConversionButtons();

    // === Predictive Search ===
    const mainContent = document.getElementById("main-content");
    let searchSection = null;

    // Persistent polite live region so screen readers hear result counts
    let searchStatusRegion = null;
    if (mainContent) {
      searchStatusRegion = document.createElement("div");
      searchStatusRegion.className = "visually-hidden";
      searchStatusRegion.setAttribute("role", "status");
      searchStatusRegion.setAttribute("aria-live", "polite");
      document.body.appendChild(searchStatusRegion);
    }

    function escapeHtml(str) {
      const div = document.createElement("div");
      div.textContent = str;
      return div.innerHTML;
    }

    // Wrap case-insensitive matches of `term` in result titles with <mark>
    function highlightSearchTerm(root, term) {
      const t = (term || "").trim();
      if (!t) return;
      const escaped = t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const rx = new RegExp(`(${escaped})`, "ig");
      root
        .querySelectorAll(".product-card__title, .collection-card__title")
        .forEach((el) => {
          el.innerHTML = escapeHtml(el.textContent).replace(
            rx,
            '<mark class="search-highlight">$1</mark>'
          );
        });
    }

    // Announce result state and highlight matches for a freshly-shown section
    function announceAndHighlight() {
      const page = searchSection?.querySelector(".search-page");
      if (!page) return;
      const state = page.dataset.searchState;
      const terms = page.dataset.searchTerms || "";
      const count = page.dataset.resultCount || "0";
      if (searchStatusRegion) {
        if (state === "results") {
          searchStatusRegion.textContent = `${count} result${
            count === "1" ? "" : "s"
          } for ${terms}`;
        } else if (state === "no-results") {
          searchStatusRegion.textContent = `No results for ${terms}`;
        } else {
          searchStatusRegion.textContent = "";
        }
      }
      if (state === "results") highlightSearchTerm(searchSection, terms);
    }

    // Track the latest request so stale (out-of-order) responses are ignored
    let searchRequestId = 0;
    const SEARCH_DEBOUNCE_MS = 250;
    const RECENT_SEARCHES_KEY = "dash:recent-searches";

    function getRecentSearches() {
      try {
        return JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY)) || [];
      } catch {
        return [];
      }
    }

    function addRecentSearch(query) {
      const q = query.trim();
      if (q.length < 2) return;
      let recent = getRecentSearches().filter(
        (item) => item.toLowerCase() !== q.toLowerCase()
      );
      recent.unshift(q);
      recent = recent.slice(0, 6);
      try {
        localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recent));
      } catch {
        /* storage unavailable — non-fatal */
      }
    }

    // Execute script tags from fetched HTML without eval(): re-create them so
    // they run in the normal global scope (this loads window.productData).
    function runScripts(container) {
      container.querySelectorAll("script").forEach((oldScript) => {
        const s = document.createElement("script");
        if (oldScript.src) {
          s.src = oldScript.src;
        } else {
          s.textContent = oldScript.textContent;
        }
        document.head.appendChild(s);
        s.remove();
      });
    }

    // Toggle the themed clear (×) button based on whether the input has text
    function toggleClearButton(input) {
      const clearBtn = input
        .closest(".search-bar")
        ?.querySelector(".search-bar__clear");
      if (clearBtn) clearBtn.hidden = input.value.trim().length === 0;
    }

    // Swap main content for a lightweight "recent searches" surface
    function showRecentPanel() {
      const recent = getRecentSearches();
      if (mainContent) mainContent.classList.add("hidden");
      if (searchSection?.parentNode) searchSection.remove();

      searchSection = document.createElement("div");
      searchSection.className = "search-recent-panel search-page page-width";

      const bar = document.createElement("div");
      bar.className = "recent-searches";
      const label = document.createElement("span");
      label.className = "recent-searches__label";
      label.textContent = "Recent searches";
      bar.appendChild(label);

      const list = document.createElement("div");
      list.className = "recent-searches__chips";
      recent.forEach((term) => {
        const chip = document.createElement("a");
        chip.className = "recent-searches__chip";
        chip.href = searchUrlFor(term);
        chip.textContent = term;
        list.appendChild(chip);
      });
      bar.appendChild(list);
      searchSection.appendChild(bar);

      // Keep focus on the input when a chip is clicked so the blur handler
      // doesn't tear down this panel before the link navigates.
      bar.addEventListener("mousedown", (e) => {
        if (e.target.closest(".recent-searches__chip")) e.preventDefault();
      });

      if (mainContent) mainContent.insertAdjacentElement("afterend", searchSection);
    }

    // Empty / too-short query: show recents if we have any, else restore page
    function showEmptyState() {
      if (getRecentSearches().length > 0) {
        showRecentPanel();
      } else {
        hideSearchResults();
      }
    }

    function showSearchSkeleton() {
      if (mainContent) mainContent.classList.add("hidden");
      if (searchSection?.parentNode) searchSection.remove();

      searchSection = document.createElement("div");
      searchSection.className = "search-skeleton page-width";
      let cards = "";
      for (let i = 0; i < 8; i++) {
        cards +=
          '<div class="search-skeleton__card">' +
          '<div class="search-skeleton__image"></div>' +
          '<div class="search-skeleton__line"></div>' +
          '<div class="search-skeleton__line search-skeleton__line--short"></div>' +
          "</div>";
      }
      searchSection.innerHTML = `<div class="search-skeleton__grid">${cards}</div>`;
      if (mainContent) mainContent.insertAdjacentElement("afterend", searchSection);
    }

    function showSearchResults(html) {
      if (mainContent) mainContent.classList.add("hidden");

      if (searchSection?.parentNode) searchSection.remove();

      const temp = document.createElement("div");
      temp.innerHTML = html;
      searchSection =
        temp.querySelector("#shopify-section-search") || temp.firstElementChild;

      if (searchSection && mainContent) {
        mainContent.insertAdjacentElement("afterend", searchSection);

        // Load product data from any inline scripts (no eval)
        runScripts(searchSection);

        setTimeout(() => {
          searchSection.classList.add("visible");

          // Initialize ProductCard instances for dynamically loaded content
          const searchProductCards =
            searchSection.querySelectorAll(".product-card");

          searchProductCards.forEach((cardEl) => {
            const productHandle = cardEl.dataset.productHandle;
            const productData = window.productData?.[productHandle];
            if (productData) {
              new ProductCard(cardEl, productData);
            }
          });

          // Re-initialize conversion buttons for dynamically loaded search results
          initializeConversionButtons(searchSection);

          // Announce result count to assistive tech + highlight matched terms
          announceAndHighlight();
        }, 10);
      }
      document.body.style.overflow = "";
    }

    function hideSearchResults() {
      if (mainContent) mainContent.classList.remove("hidden");

      if (searchSection?.parentNode) {
        searchSection.remove();
        searchSection = null;
      }
      document.body.style.overflow = "";
    }

    async function fetchPredictiveSearch(query) {
      const requestId = ++searchRequestId;
      try {
        const res = await fetch(
          `${window.Shopify.routes.root}search/suggest?q=${encodeURIComponent(
            query
          )}&section_id=search`
        );
        const html = await res.text();

        // A newer keystroke superseded this request — drop the stale response
        if (requestId !== searchRequestId) return;

        const temp = document.createElement("div");
        temp.innerHTML = html;
        const results = temp.querySelector("#shopify-section-search");

        if (!results?.textContent.trim()) {
          await fetchPredictiveSearch("show-collections");
        } else {
          showSearchResults(html);
        }
      } catch (err) {
        console.error("Predictive search failed:", err);
      }
    }

    // Minimum characters before we hit the predictive endpoint
    const SEARCH_MIN_CHARS = 2;
    // Are we on the dedicated /search page? (browse grid stays until user types)
    const onSearchPage = /\/search\/?$/.test(window.location.pathname);

    // Do we currently have real results (not the skeleton or recents) on screen?
    function hasLiveResults() {
      return (
        searchSection &&
        !searchSection.classList.contains("search-skeleton") &&
        !searchSection.classList.contains("search-recent-panel")
      );
    }

    // Initialize predictive search for all search inputs
    document.querySelectorAll('input[type="search"]').forEach((searchInput) => {
      if (mainContent) {
        let debounceTimer = null;

        toggleClearButton(searchInput);

        searchInput.addEventListener("input", (e) => {
          const val = e.target.value.trim();
          toggleClearButton(e.target);
          clearTimeout(debounceTimer);
          if (val.length < SEARCH_MIN_CHARS) {
            searchRequestId++; // invalidate any in-flight request
            showEmptyState();
            return;
          }
          // Only flash the skeleton on the first fetch — keep existing results
          // visible while refining the query to avoid per-keystroke flicker.
          if (!hasLiveResults()) showSearchSkeleton();
          debounceTimer = setTimeout(() => {
            fetchPredictiveSearch(val);
          }, SEARCH_DEBOUNCE_MS);
        });

        // Focusing an empty field surfaces recent searches (except on the
        // /search page, where the server-rendered browse grid is the surface).
        searchInput.addEventListener("focus", () => {
          if (onSearchPage) return;
          if (searchInput.value.trim().length < SEARCH_MIN_CHARS) {
            showEmptyState();
          }
        });

        searchInput.addEventListener("blur", () => {
          if (!searchInput.value.trim()) hideSearchResults();
        });
      }
    });

    // Themed clear (×) button: empty the field and reset the search surface
    document.querySelectorAll(".search-bar__clear").forEach((btn) => {
      btn.addEventListener("click", () => {
        const input = btn
          .closest(".search-bar")
          ?.querySelector('input[type="search"]');
        if (!input) return;
        input.value = "";
        toggleClearButton(input);
        searchRequestId++; // invalidate any in-flight request
        input.focus();
        showEmptyState();
      });
    });

    // Landing on /search (e.g. the mobile bottom-nav Search tab) should focus
    // the search field so the keyboard opens, without wiping the browse grid.
    if (onSearchPage) {
      const headerSearchInput = document.querySelector(
        '.header__column--search input[type="search"]'
      );
      if (headerSearchInput && !headerSearchInput.value.trim()) {
        headerSearchInput.focus({ preventScroll: true });
      }
    }

    // Persist submitted searches so they can be surfaced as recent chips
    document.querySelectorAll(".search-bar").forEach((form) => {
      form.addEventListener("submit", () => {
        const input = form.querySelector('input[type="search"]');
        if (input) addRecentSearch(input.value);
      });
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && searchSection) {
        hideSearchResults();
        document.querySelectorAll('input[type="search"]').forEach((input) => {
          input.blur();
        });
      }
    });

    // Submitting the search form hits the same whole-word matching as the
    // "See all results" link, so route it through the same URL builder. The
    // input's own value is left alone — rewriting it would show the shopper the
    // asterisk.
    document.addEventListener("submit", (e) => {
      const form = e.target.closest("form.search-bar");
      if (!form) return;
      const input = form.querySelector('input[name="q"]');
      const term = (input?.value || "").trim();
      if (!term) return;
      e.preventDefault();
      window.location.href = searchUrlFor(term);
    });

    // === Cart Modal ===
    const cartButton = document.querySelectorAll(".cart-button");

    cartButton?.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        if (window.theme?.cartType === "page") {
          window.location.href = `${window.Shopify.routes.root}cart`;
        } else {
          window.showCart();
        }
      });
    });

    // Add click handler for cart backdrop and close button
    document.addEventListener("click", (e) => {
      if (
        e.target.classList.contains("cart-backdrop") ||
        e.target.closest(".cart-content__close") ||
        e.target.classList.contains("cart-content__close") ||
        // Empty-cart action inside the drawer: dismiss, don't navigate.
        e.target.closest(".cart-empty__dismiss")
      ) {
        window.hideCart();
      }
    });

    // Add escape key handler for cart
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        if (searchSection) {
          hideSearchResults();
          document.querySelectorAll('input[type="search"]').forEach((input) => {
            input.blur();
          });
        }
        if (
          document.getElementById("cart-backdrop")?.classList.contains("active")
        ) {
          window.hideCart();
        }
      }
    });

    // === Variants List Modal Close ===
    document.addEventListener("click", (e) => {
      if (
        e.target.classList.contains("variants-list-backdrop") ||
        e.target.classList.contains("variants-list-close")
      ) {
        const variantsList = document.getElementById(
          "shopify-section-variants-list"
        );
        if (variantsList) {
          document.dispatchEvent(
            new CustomEvent("variant:change", {
              detail: { variantId: window.lastInteractedVariant },
            })
          );
          variantsList.remove();
        }
        closeModal();
      }
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        hideCart();
        const variantsList = document.getElementById(
          "shopify-section-variants-list"
        );
        if (variantsList) {
          document.dispatchEvent(
            new CustomEvent("variant:change", {
              detail: { variantId: window.lastInteractedVariant },
            })
          );
          variantsList.remove();
        }
        closeModal();
      }
    });

    // === Variant Selector ===
    document.addEventListener("change", (e) => {
      if (e.target.classList.contains("product-card__variant-select")) {
        const productCard = e.target.closest(".product-card");
        if (productCard) {
          productCard.dataset.selectedVariantId = e.target.value;
        }
      }
    });

    // === Quantity Stepper Global Handler ===
    // Optimistic: update the count instantly, sync to Shopify after a debounce.
    document.addEventListener("click", (e) => {
      const plusButton = e.target.closest(".quantity-stepper__button--plus");
      const minusButton = e.target.closest(".quantity-stepper__button--minus");
      if (!plusButton && !minusButton) return;

      const stepper = e.target.closest(".quantity-stepper");
      const variantId = stepper.dataset.variantId;
      QuantityManager.step(variantId, plusButton ? 1 : -1);
    });
  });
})();
